import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { DeletedQuestionImages } from '../../../components/forms/questionForm/types/QuestionFormTypes';
import { api } from '../../../static/api';
import { QueryAliases } from '../../../types/QueryAliases';
import { getApiErrorMessage } from '../../../utils/api/getApiErrorMessage';
import useAxiosInstance from '../../axios/useAxiosInstance';
import useToast from '../../useToast';

const useAddQuestionImages = () => {
  const axiosInstance = useAxiosInstance();
  const { t } = useTranslation();
  const { showErrorToast } = useToast();
  const queryClient = useQueryClient();

  const uploadImages = async ({
    questionId,
    mainImage,
    correctAnswerImage,
    incorrectAnswerImage,
    markerIcon,
    deletedImages,
  }: {
    questionId: string;
    mainImage: File | null;
    correctAnswerImage: File | null;
    incorrectAnswerImage: File | null;
    markerIcon?: File | null;
    deletedImages?: DeletedQuestionImages;
  }) => {
    const formData = new FormData();

    // A file replaces the image, an empty string removes it, an absent field
    // leaves it untouched on the backend — never send fields the user did not
    // change (sending an empty icon used to wipe the special pin).
    if (mainImage) {
      formData.append('image', mainImage);
    } else if (deletedImages?.mainImage) {
      formData.append('image', '');
    }

    if (correctAnswerImage) {
      formData.append('correctAnswerImage', correctAnswerImage);
    } else if (deletedImages?.correctAnswerImage) {
      formData.append('correctAnswerImage', '');
    }

    if (incorrectAnswerImage) {
      formData.append('incorrectAnswerImage', incorrectAnswerImage);
    } else if (deletedImages?.incorrectAnswerImage) {
      formData.append('incorrectAnswerImage', '');
    }

    if (markerIcon) {
      formData.append('icon', markerIcon);
    } else if (deletedImages?.markerIcon) {
      formData.append('icon', '');
    }

    const response = await axiosInstance.post(api.endpoints.QUESTION_IMAGES({ id: questionId }), formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  };

  const { mutateAsync, isLoading } = useMutation(uploadImages, {
    async onSuccess(_data, variables) {
      await queryClient.invalidateQueries([QueryAliases.SINGLE_QUESTION(variables.questionId)]);
      await queryClient.invalidateQueries([QueryAliases.QUESTIONS]);
    },
    onError: (err: AxiosError) => {
      const errorMessage = getApiErrorMessage(err) ?? t('errorMessages.addQuestionImageFailed');

      showErrorToast(errorMessage);
    },
  });

  return { addQuestionImages: mutateAsync, isLoading };
};

export default useAddQuestionImages;
